import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { Download, Loader2 } from 'lucide-react';

import { formatSkillCommand, stripLeadingSlash } from '../../../lib/skill-command-utils';
import { novaGradientClass } from '../design-system';

function getSkillOfferHandle(filename: string) {
  return filename.replace(/\.[^.]+$/, '').trim() || 'custom-skill';
}

function getSkillOfferDisplayHandle({ commandHandle, filename }: { commandHandle?: string | null; filename: string }) {
  return stripLeadingSlash(commandHandle).trim() || getSkillOfferHandle(filename);
}

export type SkillOfferAction = 'save' | 'update' | 'draft';

/** User-facing labels for the card in create and update mode. */
const actionCopy: Record<
  SkillOfferAction,
  {
    intro: string;
    description: string;
    pendingLabel: string;
    completedLabel: string;
    idleLabel: string;
  }
> = {
  save: {
    intro: "Here's your skill. Save it to add it to your / command picker.",
    description: 'Generated Nova skill bundle ready to download or save as a custom skill.',
    pendingLabel: 'Saving...',
    completedLabel: 'Saved',
    idleLabel: 'Save skill',
  },
  update: {
    intro: "Here's your updated skill. Apply it to replace the current custom skill.",
    description: 'Generated Nova skill bundle ready to download or apply to the existing custom skill.',
    pendingLabel: 'Updating...',
    completedLabel: 'Saved',
    idleLabel: 'Update skill',
  },
  draft: {
    intro: "Here's your updated skill. Save it as a draft before sharing the update.",
    description: 'Generated Nova skill bundle ready to save as an unpublished update.',
    pendingLabel: 'Saving...',
    completedLabel: 'Saved',
    idleLabel: 'Save draft',
  },
};

export function SkillOfferCard({
  action = 'save',
  commandHandle,
  filename,
  onDownload,
  onSave,
  isDownloading = false,
  isSaving = false,
  isSaved = false,
  showSave = true,
}: {
  action?: SkillOfferAction;
  commandHandle?: string | null;
  filename: string;
  onDownload?: () => void;
  onSave?: () => void;
  isDownloading?: boolean;
  isSaving?: boolean;
  isSaved?: boolean;
  showSave?: boolean;
}) {
  const handle = getSkillOfferDisplayHandle({ commandHandle, filename });
  const copy = actionCopy[action];

  return (
    <div className="flex flex-col gap-3">
      <p className="px-[10px] text-[14px] leading-[26px] text-[#262D46]">{copy.intro}</p>
      <div className="px-[10px]">
        <div className="bg-muted flex items-center justify-between gap-4 overflow-hidden rounded-[20px] border border-[#D7E0EA] px-5 py-4">
          <div className="min-w-0 space-y-1">
            <div className="truncate text-[14px] leading-5 font-semibold text-[#262D46]">
              {formatSkillCommand(handle)}
            </div>
            <div className="truncate text-[12px] leading-4 text-[#546A83]">{copy.description}</div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              data-testid="skill-offer-download-button"
              onClick={onDownload}
              disabled={!onDownload || isDownloading}
              className={cn('text-[#9CA9BA] transition-colors hover:text-[#546A83]', {
                'cursor-not-allowed opacity-50': !onDownload || isDownloading,
              })}
              aria-label={`Download ${formatSkillCommand(handle)} skill bundle`}
            >
              {isDownloading ? <Loader2 className="size-6 animate-spin" /> : <Download className="size-6" />}
            </button>
            {showSave ? (
              <Button
                type="button"
                data-testid="skill-offer-save-button"
                onClick={onSave}
                disabled={!onSave || isSaving || isSaved}
                className={cn(
                  'text-wtw-ultraviolet-850 rounded-full border-0 px-4 py-2 text-[14px] font-semibold',
                  novaGradientClass.aiBackground,
                  'shadow-none hover:opacity-95',
                  isSaved && 'bg-[#E7EDF3] text-[#546A83] hover:opacity-100',
                )}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {copy.pendingLabel}
                  </>
                ) : isSaved ? (
                  copy.completedLabel
                ) : (
                  copy.idleLabel
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
